import CheckIcon from "@mui/icons-material/Check";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditStatus,
  DebtType,
  PaymentPlansDetail,
} from "../../Models/Credit";
import { ROUTE_PATHS } from "../../Routes/constants";
import { getPaymentPlansDetail } from "../../Services/CreditService";
import { sendEvent } from "../../Utils/analytics";
import {
  BORDER_LIGHT,
  CARD_SHADOW,
  DANGER,
  DANGER_BORDER,
  INK,
  SUCCESS,
  SUCCESS_BG,
  TEXT_MUTED,
} from "../../Utils/designTokens";
import { getRemoteText, getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import CModal from "../CModal/CModal";
import FormatAmount, { FormatAmountSymbol } from "../FormatData/FormatAmount";
import FormatDateToTR from "../FormatData/FormatDate";
import DebtDetail from "./DebtDetail";
import PendingDebtDetailModal from "./PendingDebtDetailModal";
import SnoozeDialogCalendar from "./SnoozeDialogCalendar";
require("moment/locale/tr");

interface Props {
  debt: DebtType;
  onPayClick?: () => void;
  paymentPlans: DebtType[];
  /** CRL-3241 multi-select (pending tab on home page only). */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** CRL-3241: pressing the card opens the pending payment detail modal (home tabs only). */
  detailOnPress?: boolean;
}

/** Whole days late; 0 when not late yet. */
const daysLate = (dueDate: string): number => {
  const diff = moment().startOf("day").diff(moment(dueDate).startOf("day"), "days");
  return Math.max(diff, 0);
};

/** Whole days until the due date (negative when past). */
const daysUntil = (dueDate: string): number =>
  moment(dueDate).startOf("day").diff(moment().startOf("day"), "days");

const Debt = ({
  debt,
  onPayClick,
  paymentPlans,
  selectable = false,
  selected = false,
  onToggleSelect,
  detailOnPress = false,
}: Props) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [debtDetail, setDebtDetail] = useState<PaymentPlansDetail>();
  const setPaymentMethod = useAuthState((state) => state.setPaymentMethod);
  const setIsAllPayment = useAuthState((state) => state.setIsAllPayment);
  const navigate = useNavigate();
  const [postponeDialogOpen, setPostponeDialogOpen] = useState(false);
  const [pendingDetailOpen, setPendingDetailOpen] = useState(false);

  const isLate = debt.status === CreditStatus.LATE;
  const isPaid = debt.status === CreditStatus.PAID;

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handlePlanDetail = async (id: number) => {
    setLoading(true);
    try {
      const res = await getPaymentPlansDetail(id);
      if (res) {
        setDebtDetail(res);
        setModalOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Modals render inside the card, so their (portaled) clicks bubble back to
  // the card in the React tree; bail while any overlay is already open.
  const handleCardPress = () => {
    if (!detailOnPress || isPaid || pendingDetailOpen || postponeDialogOpen || isModalOpen) {
      return;
    }
    sendEvent(`view_pending_payment_detail`, {
      creditId: debt.creditId,
      payment_plan_id: debt.id,
    });
    setPendingDetailOpen(true);
  };

  const handlePay = () => {
    sendEvent(`click_pay_credit_button`, {
      creditId: debt.creditId,
      payment_plan_id: debt.id,
      total_remaining_amount: debt.totalRemainingAmount,
      shop_name: debt.shop.name,
      total_paid_amount: debt.totalPaidAmount,
      status: debt.status,
      credit_amount: debt.creditAmount,
    });
    setPaymentMethod({ type: 2, creditId: debt.creditId });
    if (onPayClick) {
      onPayClick();
    } else {
      setIsAllPayment(false);
      navigate(ROUTE_PATHS.CHECKOUT);
    }
  };

  return (
    <Stack
      onClick={handleCardPress}
      sx={{
        minWidth: "300px",
        maxWidth: "900px",
        borderRadius: "20px",
        background: "#FFF",
        border: selected
          ? `1.5px solid ${INK}`
          : `1.5px solid ${isLate ? DANGER_BORDER : BORDER_LIGHT}`,
        boxShadow: CARD_SHADOW,
        padding: "20px",
        margin: "0 15px",
        position: "relative",
        cursor: detailOnPress && !isPaid ? "pointer" : "default",
        "@media (min-width: 768.5px)": {
          margin: 0,
        },
      }}
    >
      {/* Header: logo + name + işlem no (+ selection checkbox) */}
      <Box key={"header"} display={"flex"} flexDirection={"row"} alignItems={"center"} gap={"12px"}>
        <img
          src={debt.shop.logoUrl}
          alt={debt.shop.name}
          style={{
            objectFit: "cover",
            width: "44px",
            height: "44px",
            borderRadius: "10px",
            flexShrink: 0,
          }}
        />
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <h2 className="text-[15px] lg:text-base font-semibold leading-tight truncate">
            {debt.shop.name}
          </h2>
          <div className="text-xs text-[#94A3B8] pt-[2px]">
            {getRemoteValue("transaction_number")} {debt.creditId}
          </div>
        </Stack>
        {selectable && (
          <Box
            role="checkbox"
            aria-checked={selected}
            aria-label={getRemoteText("select_payment", "Ödemeyi seç")}
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect?.();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggleSelect?.();
              }
            }}
            sx={{
              width: "24px",
              height: "24px",
              borderRadius: "7px",
              flexShrink: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              alignSelf: "flex-start",
              border: selected ? `1.5px solid ${INK}` : "1.5px solid #CBD5E1",
              backgroundColor: selected ? INK : "#FFF",
              transition: "background-color 120ms ease, border-color 120ms ease",
            }}
          >
            {/* CRL-3241: checkmark her zaman görünür (hover'a bağlı değil) */}
            {selected && <CheckIcon sx={{ fontSize: 16, color: "#FFF" }} />}
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box key={"body"} display={"flex"} flexDirection={"column"} pt={2.5} gap={2}>
        <Stack flexDirection={"row"} justifyContent={"space-between"} alignItems={"end"}>
          <Stack>
            <div className="text-xs font-normal text-[#94A3B8]">
              {getRemoteValue("last_payment_date")}
            </div>
            <div className="text-[15px] font-semibold text-cardValue pt-[2px]">
              {FormatDateToTR(debt.dueDate)}
            </div>
          </Stack>
          <Stack direction={"row"} alignItems={"center"} gap={"6px"} flexWrap={"wrap"} justifyContent={"end"}>
            {!isPaid && debt.totalPaidAmount !== 0 && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                sx={{
                  backgroundColor: "#ECEDEF",
                  color: "#5A6478",
                  borderRadius: "999px",
                  padding: "4px 10px",
                }}
              >
                <span className="text-xs font-semibold whitespace-nowrap">
                  {getRemoteText("partial_paid_label", "Kısmi ödendi")}
                </span>
              </Stack>
            )}
            {isLate && daysLate(debt.dueDate) > 0 && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={"4px"}
                sx={{
                  backgroundColor: "#FEF2F2",
                  color: DANGER,
                  borderRadius: "999px",
                  padding: "4px 10px",
                }}
              >
                <ScheduleIcon sx={{ fontSize: 13 }} />
                <span className="text-xs font-semibold whitespace-nowrap">
                  {getRemoteText(
                    "days_late_x",
                    "{val} gün gecikti",
                    daysLate(debt.dueDate).toString()
                  )}
                </span>
              </Stack>
            )}
            {!isPaid && !isLate && (daysUntil(debt.dueDate) === 0 || daysUntil(debt.dueDate) === 1) && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={"4px"}
                sx={{
                  backgroundColor: "#FFF7E6",
                  color: "#B7791F",
                  borderRadius: "999px",
                  padding: "4px 10px",
                }}
              >
                <ScheduleIcon sx={{ fontSize: 13 }} />
                <span className="text-xs font-semibold whitespace-nowrap">
                  {daysUntil(debt.dueDate) === 0
                    ? getRemoteText("due_today", "Bugün")
                    : getRemoteText("due_tomorrow", "Yarın")}
                </span>
              </Stack>
            )}
          </Stack>
          {isPaid && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={"4px"}
              sx={{
                backgroundColor: SUCCESS_BG,
                color: SUCCESS,
                borderRadius: "999px",
                padding: "4px 10px",
              }}
            >
              <CheckIcon sx={{ fontSize: 13 }} />
              <span className="text-xs font-semibold whitespace-nowrap">
                {getRemoteText("paid_label", "Ödendi")}
              </span>
            </Stack>
          )}
        </Stack>

        <Stack>
          <div className="flex items-center">
            <div className="text-xs font-normal text-[#94A3B8]">
              {isPaid
                ? getRemoteValue("total_pay")
                : getRemoteValue("remaining_pay")}
            </div>
            {debt.totalPaidAmount !== 0 && !isPaid && (
              <Tooltip
                enterTouchDelay={0}
                placement="top-end"
                title={`${getRemoteValue("your_spending")}${FormatAmount(
                  debt.totalPaidAmount
                )} ${getRemoteValue("your_amount_paid_partial")}`}
              >
                <IconButton sx={{ p: 0, px: 1 }} onClick={(e) => e.stopPropagation()}>
                  <img
                    src={"/icons/info-icon.svg"}
                    style={{ color: "#74788D" }}
                    alt=""
                  />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div
            className="text-[22px] font-bold pt-[2px]"
            style={{ color: isLate ? DANGER : INK }}
          >
            {FormatAmountSymbol(
              isPaid ? debt.totalAmount : debt.remainingPaymentAmount
            )}
          </div>
        </Stack>
      </Box>

      {/* Footer */}
      <Box pt={2.5} key={"footer"} display={"flex"}>
        {isPaid ? (
          <CLoadingButton
            fullWidth
            size="medium"
            variant="contained"
            loading={loading}
            sx={{
              backgroundColor: "#F1F5F9",
              color: INK,
              "&:hover": { backgroundColor: "#E2E8F0" },
            }}
            onClick={() => handlePlanDetail(debt.id)}
          >
            {getRemoteValue("see_detail")}
          </CLoadingButton>
        ) : (
          <Stack
            direction={"row"}
            spacing={2}
            sx={{ width: "100%" }}
            alignItems={"center"}
          >
            {debt.snoozeAvailable ? (
              <CLoadingButton
                fullWidth
                size="medium"
                variant="outlined"
                sx={{
                  flex: 1,
                  // design: card "Ertele" is a text button
                  border: "none",
                  color: TEXT_MUTED,
                  "&:hover": {
                    border: "none",
                    backgroundColor: "#ECEDEF",
                    color: INK,
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  sendEvent('click_snooze_button');
                  setPostponeDialogOpen(true);
                }}
              >
                {getRemoteValue("snooze")}
              </CLoadingButton>
            ) : (
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"6px"}
                sx={{ flex: 1, color: TEXT_MUTED }}
              >
                <ScheduleIcon sx={{ fontSize: 15 }} />
                <span className="text-sm font-medium whitespace-nowrap">
                  {getRemoteText("not_snoozable", "Ertelenemez")}
                </span>
              </Stack>
            )}
            <CLoadingButton
              size="large"
              variant="contained"
              sx={{ flex: 1.4 }}
              onClick={(e) => {
                e.stopPropagation();
                handlePay();
              }}
            >
              {getRemoteValue("pay")}
            </CLoadingButton>
          </Stack>
        )}
      </Box>
      {isModalOpen && (
        <CModal
          open={isModalOpen}
          onClose={handleModalClose}
          contentSx={{
            "@media (min-width: 768.5px)": {
              width: "min(480px, 92vw)",
              borderRadius: "24px",
            },
          }}
        >
          <DebtDetail debtDetail={debtDetail} shop={debt.shop} />
        </CModal>
      )}
      <PendingDebtDetailModal
        open={pendingDetailOpen}
        onClose={() => setPendingDetailOpen(false)}
        debt={debt}
        onPay={() => {
          setPendingDetailOpen(false);
          handlePay();
        }}
        onSnooze={() => {
          setPendingDetailOpen(false);
          sendEvent('click_snooze_button');
          setPostponeDialogOpen(true);
        }}
      />
      <SnoozeDialogCalendar
        open={postponeDialogOpen}
        onClose={() => setPostponeDialogOpen(false)}
        debt={{
          id: debt.id,
          maxSnoozeDuration: debt.maxSnoozeDuration,
          creditId: debt.creditId,
          shop: debt.shop,
          dueDate: new Date(debt.dueDate),
          isLate: debt.status === CreditStatus.LATE,
          amount: debt.remainingPaymentAmount,
        }}
        badgeDates={paymentPlans.filter(plan => plan.id === debt.id).map(plan => new Date(plan.dueDate))}
      />
    </Stack>
  );
};

export default Debt;
